import * as React from 'react';
import Button from "@material-ui/core/Button";
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useEffect, useState } from "react";
import axios from "axios";
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Input,
  Box,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Chip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { 
  CameraAlt, 
  Delete, 
  FlashOn
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import './resources.css';

export default function FormDialog({ open, handleClose, data, onChange, handleImageChange, handleFormSubmit, axiosConfig, rowUpdate }) {
  const [resourceTypes, setResourceTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Screenshot-related state (only for create, not update)
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [imageSource, setImageSource] = useState(''); // 'upload', 'screenshot', or ''
  const [autoScreenshotEnabled, setAutoScreenshotEnabled] = useState(true);
  const [urlDebounceTimer, setUrlDebounceTimer] = useState(null);
  const [websiteError, setWebsiteError] = useState(false);

  const fetchData = async () => {
    // Get all resource types
    const _resourceTypes = await axios.get(`/resource-type`, axiosConfig);
    setResourceTypes(_resourceTypes.data);
    // Get all categories
    const _categories = await axios.get(`/category`, axiosConfig);
    setCategories(_categories.data)
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Reset screenshot state when dialog opens/closes
  useEffect(() => {
    if (open && !rowUpdate) {
      // Only enable screenshot features for create (not update)
      setImageSource('');
      setWebsiteError(false);
      setAutoScreenshotEnabled(true);
      
      // Auto-fill owner with current user ID for create mode
      const currentUserId = localStorage.getItem('id');
      console.log('🔧 Setting owner for create mode:', currentUserId);
      
      if (currentUserId) {
        // Set owner ID in form data
        onChange({ target: { name: 'owner', value: currentUserId } });
        // Auto-approve when user creates their own resource
        onChange({ target: { name: 'ownerApproval', value: true } });
        console.log('✅ Owner field set to:', currentUserId);
        console.log('✅ Owner approval set to: true (auto-approved)');
        
        // If user email is not in localStorage, try to fetch it
        if (!localStorage.getItem('email')) {
          fetchCurrentUserInfo(currentUserId);
        }
      } else {
        console.warn('⚠️ No user ID found in localStorage');
      }
    } else {
      // Reset state when dialog closes or when updating
      setScreenshotLoading(false);
      setImageSource('');
      setAutoScreenshotEnabled(true);
      setWebsiteError(false);
      if (urlDebounceTimer) clearTimeout(urlDebounceTimer);
    }
  }, [open, rowUpdate]);

  // Fetch current user information if not available in localStorage
  const fetchCurrentUserInfo = async (userId) => {
    try {
      // You can implement this API call to get user info if needed
      // const response = await axios.get(`/user/${userId}`, axiosConfig);
      // if (response.data.email) {
      //   localStorage.setItem('email', response.data.email);
      // }
      console.log('📧 User email fetch - implement API call if needed for userId:', userId);
    } catch (error) {
      console.error('❌ Error fetching user info:', error);
    }
  };

  // URL validation function
  const validateURL = (url) => {
    if (!url || !url.trim()) {
      return { isValid: false, message: "Please enter a website URL" };
    }

    const cleanUrl = url.trim();
    
    if (!cleanUrl.match(/^https?:\/\//i)) {
      return { isValid: false, message: "URL must start with http:// or https://" };
    }

    try {
      const urlObj = new URL(cleanUrl);
      
      if (!urlObj.hostname || urlObj.hostname.length < 3) {
        return { isValid: false, message: "Invalid website domain" };
      }

      if (!urlObj.hostname.includes('.')) {
        return { isValid: false, message: "Invalid website domain format" };
      }

      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { isValid: false, message: "Only HTTP and HTTPS URLs are supported" };
      }

      if (urlObj.hostname.startsWith('.') || urlObj.hostname.endsWith('.')) {
        return { isValid: false, message: "Invalid website domain format" };
      }

      return { isValid: true, message: "" };
    } catch (error) {
      return { isValid: false, message: "Invalid URL format" };
    }
  };

  // Enhanced form change handler with debounced auto-screenshot (only for create)
  const handleFormChange = (event) => {
    onChange(event);
    
    if (event.target.name === 'website' && !rowUpdate && autoScreenshotEnabled) {
      const websiteValue = event.target.value;
      console.log('🔍 Website field changed:', websiteValue);
      
      setWebsiteError(false);
      
      if (urlDebounceTimer) clearTimeout(urlDebounceTimer);
      
      const newTimer = setTimeout(() => {
        console.log('🕒 Debounce timer triggered, calling autoGenerateScreenshot...');
        autoGenerateScreenshot(websiteValue);
      }, 2000);
      
      setUrlDebounceTimer(newTimer);
    }
  };

  // Auto screenshot generation (only for create mode)
  const autoGenerateScreenshot = async (websiteUrl) => {
    if (rowUpdate || !autoScreenshotEnabled || imageSource === 'upload') return;
    
    const validationResult = validateURL(websiteUrl);
    if (!validationResult.isValid) return;

    console.log('✅ Starting auto screenshot generation...');
    setScreenshotLoading(true);
    
    try {
      console.log('📡 Making HTTP request to /resource/generate-screenshot');
      const response = await axios.post('/resource/generate-screenshot', 
        { url: websiteUrl }, 
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      console.log('✅ Screenshot response received:', response.data);

      if (response.data.data?.base64 && imageSource !== 'upload') {
        onChange({ target: { name: 'image', value: response.data.data.base64 } });
        setImageSource('screenshot');
        setWebsiteError(false);
      }
    } catch (error) {
      console.error('❌ Auto screenshot generation failed:', error);
      // For auto-generation, only show accessibility errors
      if (error.response?.data?.message?.includes('not accessible') || 
          error.response?.data?.message?.includes('does not exist')) {
        setWebsiteError(true);
        toast.error('Website cannot be reached. Please check the URL.');
      }
    } finally {
      setScreenshotLoading(false);
    }
  };

  // Manual screenshot generation
  const generateScreenshot = async () => {
    const validationResult = validateURL(data.website);
    if (!validationResult.isValid) {
      toast.error(validationResult.message);
      return;
    }

    console.log('🔧 Manual screenshot generation started');
    setScreenshotLoading(true);
    
    try {
      const response = await axios.post('/resource/generate-screenshot', 
        { url: data.website }, 
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      if (response.data.data?.base64) {
        onChange({ target: { name: 'image', value: response.data.data.base64 } });
        setImageSource('screenshot');
        setWebsiteError(false);
        toast.success("Screenshot generated and set as resource image!");
      }
    } catch (error) {
      console.error('❌ Manual screenshot generation failed:', error);
      toast.error(error.response?.data?.message || 'Failed to generate screenshot. Please try again.');
    } finally {
      setScreenshotLoading(false);
    }
  };

  // Enhanced image change handler
  const handleImageChangeWithSource = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange({ target: { name: 'image', value: reader.result } });
        if (!rowUpdate) {
          setImageSource('upload');
          setAutoScreenshotEnabled(false);
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // Remove image function (only for create)
  const removeImage = () => {
    onChange({ target: { name: 'image', value: '' } });
    if (!rowUpdate) {
      setImageSource('');
      setAutoScreenshotEnabled(true);
    }
  };

  return (
    <div className="section">
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="title" id="alert-dialog-title">{rowUpdate ? "Update Resource Type" : "Add Resource Type"}</DialogTitle>
        <DialogContent>
          <form>
            <TextField name="code" value={data.code || ''} onChange={handleFormChange} placeholder="Enter Code" label="Code" variant="outlined" margin="dense" fullWidth />
            <TextField name="name" value={data.name || ''} onChange={handleFormChange} placeholder="Enter Name" label="Name" variant="outlined" margin="dense" fullWidth />
            <TextField name="description" value={data.description || ''} onChange={handleFormChange} placeholder="Enter Description" label="Description" variant="outlined" margin="dense" fullWidth />
            
            {/* Enhanced Website Input with Screenshot Controls (only for create) */}
            {!rowUpdate ? (
              <Box mt={2} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1">Website</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {screenshotLoading && (
                      <Box display="flex" alignItems="center">
                        <CircularProgress size={16} style={{ marginRight: 8 }} />
                        <Typography variant="caption" color="textSecondary">
                          Generating screenshot...
                        </Typography>
                      </Box>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoScreenshotEnabled}
                          onChange={(e) => setAutoScreenshotEnabled(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Auto-screenshot
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
                
                <Box display="flex" gap={2} alignItems="flex-start">
                  <TextField
                    name="website"
                    value={data.website || ''}
                    onChange={handleFormChange}
                    placeholder="https://www.example.com"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={websiteError}
                    style={{ flexGrow: 1 }}
                  />
                  
                  <IconButton
                    onClick={generateScreenshot}
                    disabled={screenshotLoading || !data.website}
                    color="primary"
                    title="Generate screenshot manually"
                    style={{ 
                      border: '1px solid #1976d2', 
                      borderRadius: 4,
                      width: 48,
                      height: 48,
                      marginTop: 8,
                      flexShrink: 0
                    }}
                  >
                    {screenshotLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CameraAlt />
                    )}
                  </IconButton>
                </Box>
                
                {websiteError && (
                  <Alert severity="error" variant="outlined" style={{ marginTop: 8 }}>
                    <Typography variant="caption">
                      This website cannot be reached. Please check the URL and try again.
                    </Typography>
                  </Alert>
                )}
                
                {autoScreenshotEnabled && data.website && !websiteError && (
                  <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                    <FlashOn style={{ fontSize: 12, marginRight: 4 }} />
                    Auto-screenshot enabled - screenshots will generate automatically
                  </Typography>
                )}
              </Box>
            ) : (
              // Simple website field for update mode
              <TextField name="website" value={data.website || ''} onChange={e => onChange(e)} placeholder="Enter Website" label="Website" variant="outlined" margin="dense" fullWidth />
            )}

            <FormControl variant="outlined" fullWidth margin="normal" required>
              <InputLabel>Type</InputLabel>
              <Select name="type" label="Type" value={data.type?._id || data.type || ''} onChange={e => onChange(e)}>
                {resourceTypes.map((type, index) => (
                  <MenuItem key={index} value={type._id}>
                    {type.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" fullWidth margin="normal" required>
              <InputLabel>Category</InputLabel>
              <Select name="category" label="Category" value={data.category?._id || data.category || ''} onChange={e => onChange(e)}>
                {categories.map((category, index) => (
                  <MenuItem key={index} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={2} mb={2}>
              <FormControl variant="outlined" fullWidth required>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                  value={data.status || ''}
                onChange={e => onChange(e)}
                label="Status"
                  displayEmpty={false}
                inputProps={{ 'aria-label': 'Status' }}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            </Box>

            <TextField
              name="ownerEmail"
              value={!rowUpdate ? (localStorage.getItem('email') || 'Current User') : (data.owner?.email || '')}
              label="Owner Email"
              variant="outlined"
              margin="dense"
              disabled
              fullWidth
              helperText={!rowUpdate ? "Automatically set to current logged-in user" : ""}
            />

            <Box mt={2} mb={2}>
              {!rowUpdate ? (
                // For create mode, show as automatically approved
                <TextField
                  value="Approved"
                  label="Owner Approval"
                  variant="outlined"
                  disabled
                  fullWidth
                  helperText="Automatically approved when owner creates their own resource"
                />
              ) : (
                // For update mode, show the select field
                <FormControl variant="outlined" fullWidth required>
                  <InputLabel id="owner-approval-label">Owner Approval</InputLabel>
                  <Select
                    labelId="owner-approval-label"
                    name="ownerApproval"
                    value={data.ownerApproval || ''}
                    onChange={e => onChange(e)}
                    label="Owner Approval"
                    displayEmpty={false}
                    inputProps={{ 'aria-label': 'Owner Approval' }}
                  >
                    <MenuItem value={true}>Approved</MenuItem>
                    <MenuItem value={false}>Not Approved</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Enhanced Image Section (only for create) */}
            {!rowUpdate ? (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>Image</Typography>
                
                {/* Current Image Display */}
                {data?.image && (
                  <Box mb={2}>
                    <img
                      src={data.image}
                      alt="Resource"
                      style={{ 
                        maxHeight: "150px", 
                        maxWidth: "100%", 
                        border: '1px solid #ddd', 
                        borderRadius: 4,
                        objectFit: 'cover'
                      }}
                    />
                    <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        {imageSource === 'screenshot' && (
                          <Chip
                            icon={<CameraAlt />}
                            label="Screenshot from website"
                            variant="outlined"
                            color="primary"
                            size="small"
                          />
                        )}
                        {imageSource === 'upload' && (
                          <Chip
                            label="Uploaded file"
                            variant="outlined"
                            color="secondary"
                            size="small"
                          />
                        )}
                      </Box>
                      <IconButton
                        onClick={removeImage}
                        size="small"
                        color="secondary"
                        title="Remove image"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Auto-screenshot status indicators */}
                {!data?.image && autoScreenshotEnabled && data.website && !screenshotLoading && (
                  <Box textAlign="center" p={2} border="1px dashed #ccc" borderRadius={1} mb={2} bgcolor="#f5f5f5">
                    <FlashOn color="primary" style={{ fontSize: 32, marginBottom: 8 }} />
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Auto-screenshot is enabled
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Screenshot will generate automatically when you finish typing the URL
                    </Typography>
                  </Box>
                )}

                {!data?.image && screenshotLoading && (
                  <Box textAlign="center" p={2} border="1px dashed #ccc" borderRadius={1} mb={2} bgcolor="#f5f5f5">
                    <CircularProgress style={{ marginBottom: 8 }} />
                    <Typography variant="body2" color="textSecondary">
                      Generating screenshot from website...
                    </Typography>
                  </Box>
                )}

                {/* File Upload */}
                <Box display="flex" gap={1} alignItems="flex-end">
                  <Box flexGrow={1}>
                    <InputLabel style={{ marginBottom: 8 }}>
                      {data?.image ? "Update Image File" : "Upload Image File"}
                    </InputLabel>
                    
                    {!data?.image ? (
                      // Show normal file input when no image exists
                      <>
                        <Input 
                          type="file" 
                          id="image-create" 
                          name="image" 
                          onChange={handleImageChangeWithSource}
                          inputProps={{ accept: "image/*" }}
                          fullWidth
                        />
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Upload an image file or use auto-screenshot above
                          </Typography>
                          {!autoScreenshotEnabled && (
                            <Button
                              size="small"
                              startIcon={<FlashOn />}
                              onClick={() => setAutoScreenshotEnabled(true)}
                              style={{ textTransform: 'none' }}
                            >
                              Enable auto-screenshot
                            </Button>
                          )}
                        </Box>
                      </>
                    ) : (
                      // Show custom button when image already exists
                      <>
                        <input
                          type="file"
                          id="image-update"
                          name="image"
                          onChange={handleImageChangeWithSource}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="image-update">
                          <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            style={{
                              padding: '12px 16px',
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              color: '#666',
                              borderColor: '#ccc'
                            }}
                          >
                            Choose file to replace current {imageSource === 'screenshot' ? 'screenshot' : 'image'}
                          </Button>
                        </label>
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Select a new file to replace the current {imageSource === 'screenshot' ? 'screenshot' : 'uploaded image'}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              // Original image section for update mode
              <>
            <InputLabel htmlFor="image">Image</InputLabel>
            <FormControl variant="outlined" fullWidth margin="normal">
              <Input type="file" id="image" name="image" onChange={handleImageChange} />
              {data?.image?.url && (
                <img src={data.image.url} alt="Image" style={{ maxWidth: "100%", height: "auto" }} />
              )}
            </FormControl>
              </>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">Cancel</Button>
          <Button className="createbutton" onClick={(e) => handleFormSubmit(e)} variant="contained">
            {rowUpdate ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}